import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { storeRtoRecommendations, getStoredRtoRecommendations } from "../../../utils/recsCache";
import { requestRecommendations } from "../../../utils/requestRecommendations";

const sectionCardShellStyle = {
  background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
  borderRadius: "16px",
  border: "1px solid var(--card-border)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
};

const SectionCardIcon = ({ icon, style }) => (
  <div
    style={{
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      background: "rgba(59,130,246,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      color: "#3b82f6",
      flexShrink: 0,
      ...style,
    }}
  >
    {icon}
  </div>
);

const SectionCard = ({ children, title, icon }) => (
  <div
    style={{
      ...sectionCardShellStyle,
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <SectionCardIcon icon={icon} />
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const SectionCardAccordion = ({
  children,
  title,
  icon,
  iconStyle,
  defaultOpen = false,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div id={id} style={{ ...sectionCardShellStyle, overflow: "hidden", scrollMarginTop: "20px" }}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "24px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <SectionCardIcon icon={icon} style={iconStyle} />
          <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
        </div>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
          style={{ color: "#64748b", fontSize: "14px", flexShrink: 0 }}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "0 24px 24px",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const formatNumber = (value) => {
  if (typeof value !== "number") return "—";
  if (value >= 1000) return value.toLocaleString();
  return Number.isInteger(value) ? value : value.toFixed(2);
};

const RtoRecommendations = ({ reportData }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.rto" });
  const [recommendationDetails, setRecommendationDetails] = useState(() => {
    const reportId = reportData?.id;
    return reportId ? getStoredRtoRecommendations(reportId) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const reportId = reportData?.id;
    if (!reportId) {
      setRecommendationDetails(null);
      return;
    }

    const stored = getStoredRtoRecommendations(reportId);
    if (stored) {
      setRecommendationDetails(stored);
    } else {
      setRecommendationDetails(null);
    }
  }, [reportData?.id]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get report ID from reportData
      const reportId = reportData?.id;
      if (!reportId) {
        throw new Error(t("reportIdNotFound"));
      }

      const result = await requestRecommendations({
        recommFor: "rto",
        reportId,
        reportData,
        fallbackMessage: t("noRecommendationsAvailable"),
      });
      setRecommendationDetails(result);

      if (result && Object.keys(result).length > 0) {
        storeRtoRecommendations(reportId, result);
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show button to load recommendations if not loaded yet
  if (!recommendationDetails && !loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-lightbulb" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>

        {error && (
          <div style={{
            fontSize: "14px",
            color: "#ef4444",
            marginBottom: "20px",
            padding: "12px 20px",
            background: "rgba(239, 68, 68, 0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(239, 68, 68, 0.2)"
          }}>
            {t("noRecommendationsFound")}
          </div>
        )}
        <button
          onClick={fetchRecommendations}
          disabled={loading}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            opacity: loading ? 0.6 : 1
          }}
          onMouseOver={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
            }
          }}
          onMouseOut={(e) => {
            if (!loading) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
            }
          }}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
              {t("loading")}
            </>
          ) : (
            <>
              <i className="fas fa-download" style={{ marginRight: "8px" }}></i>
              {t("loadRecommendations")}
            </>
          )}
        </button>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(59, 130, 246, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: "28px", color: "#3b82f6" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("loadingRecommendations")}
        </div>
      </div>
    );
  }

  // Show error if recommendations failed to load
  if (error && !recommendationDetails) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(239, 68, 68, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: "28px", color: "#ef4444" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600, marginBottom: "20px" }}>
          {t("noRecommendationsFound")}
        </div>
        <button
          onClick={fetchRecommendations}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)"
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
          }}
        >
          <i className="fas fa-redo" style={{ marginRight: "8px" }}></i>
          {t("retry")}
        </button>
      </div>
    );
  }

  // Show empty state if recommendations loaded but empty
  if (recommendationDetails && Object.keys(recommendationDetails).length === 0) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-compass" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noRecommendationsAvailable")}
        </div>
      </div>
    );
  }

  const {
    initial_response_time_ms,
    performance_improvement_plan,
    recommendation_tables,
    summary,
    methodology
  } = recommendationDetails || {};

  const tierColors = {
    "Ultra Low-Latency": { bg: "rgba(34, 197, 94, 0.15)", border: "rgba(34, 197, 94, 0.3)", text: "#22c55e", gradient: "linear-gradient(135deg, #065f46, #22c55e)" },
    "Real-Time": { bg: "rgba(59, 130, 246, 0.15)", border: "rgba(59, 130, 246, 0.3)", text: "#3b82f6", gradient: "linear-gradient(135deg, #1e3a8a, #3b82f6)" },
    "Business-Critical": { bg: "rgba(245, 158, 11, 0.15)", border: "rgba(245, 158, 11, 0.3)", text: "#f59e0b", gradient: "linear-gradient(135deg, #92400e, #f97316)" },
    "Enterprise-Basic": { bg: "rgba(107, 114, 128, 0.15)", border: "rgba(107, 114, 128, 0.3)", text: "#6b7280", gradient: "linear-gradient(135deg, #374151, #6b7280)" }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px" }}>

      {/* Performance Improvement Plan and Summary Side by Side */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "24px", alignItems: "stretch" }}>
        {/* Performance Improvement Plan Section */}
        {performance_improvement_plan && (
          <SectionCard title={t("performanceImprovementPlan")} icon={<i className="fas fa-chart-line"></i>}>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {initial_response_time_ms && (
                <div style={{
                  background: "rgba(59, 130, 246, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(59, 130, 246, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>
                    {t("initialResponseTime")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {formatNumber(initial_response_time_ms)}ms
                  </div>
                </div>
              )}
              {performance_improvement_plan.total_improvement && (
                <div style={{
                  background: "rgba(34, 197, 94, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(34, 197, 94, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", fontWeight: 600 }}>
                    {t("totalImprovement")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {performance_improvement_plan.total_improvement}
                  </div>
                </div>
              )}
              {performance_improvement_plan.final_target && (
                <div style={{
                  background: "rgba(139, 92, 246, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(139, 92, 246, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", fontWeight: 600 }}>
                    {t("targetResponseTime")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {formatNumber(performance_improvement_plan.final_target)}ms
                  </div>
                </div>
              )}
              {performance_improvement_plan.improvement_per_step && (
                <div style={{
                  background: "rgba(245, 158, 11, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(245, 158, 11, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", fontWeight: 600 }}>
                    {t("improvementPerStep")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {performance_improvement_plan.improvement_per_step}
                  </div>
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* Summary Section */}
        {summary && (
          <SectionCard title={t("optimizationSummary")} icon={<i className="fas fa-clipboard-list"></i>}>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {summary.tables_count && (
                <div style={{
                  background: "rgba(59, 130, 246, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(59, 130, 246, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b82f6", fontWeight: 600 }}>
                    {t("optimizationSteps")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {summary.tables_count}
                  </div>
                </div>
              )}
              {summary.recommended_starting_point && (
                <div style={{
                  background: "rgba(34, 197, 94, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(34, 197, 94, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#22c55e", fontWeight: 600 }}>
                    {t("startingPoint")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {summary.recommended_starting_point}
                  </div>
                </div>
              )}
              {summary.max_performance_tier && (
                <div style={{
                  background: "rgba(139, 92, 246, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(139, 92, 246, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#8b5cf6", fontWeight: 600 }}>
                    {t("maxTier")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {summary.max_performance_tier}
                  </div>
                </div>
              )}
              {summary.estimated_cost_range && (
                <div style={{
                  background: "rgba(245, 158, 11, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(245, 158, 11, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#f59e0b", fontWeight: 600 }}>
                    {t("costRange")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {summary.estimated_cost_range}
                  </div>
                </div>
              )}
              {summary.implementation_timeline && (
                <div style={{
                  background: "rgba(239, 68, 68, 0.04)",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  border: "1px solid rgba(239, 68, 68, 0.12)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#ef4444", fontWeight: 600 }}>
                    {t("timeline")}
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: 600, color: "var(--card-text)" }}>
                    {summary.implementation_timeline}
                  </div>
                </div>
              )}
            </div>
          </SectionCard>
        )}
      </div>

      {/* Methodology Section */}
      {methodology && (
        <SectionCardAccordion title={t("methodology")} icon={<i className="fas fa-calculator"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Time Allocation - Full Width Stacked Bar */}
            <div style={{ background: "rgba(59, 130, 246, 0.04)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(59, 130, 246, 0.12)" }}>
              <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "white", marginBottom: "14px", fontWeight: 600 }}>
                {t("timeAllocation")}
              </div>
              {(() => {
                // Parse percentages from values - always just 2 keys: network_time_allocation and processing_time_allocation
                const parsePercentage = (value) => {
                  if (!value) return 0;
                  const numStr = value.toString().replace(/[^0-9.]/g, '');
                  return parseFloat(numStr) || 0;
                };

                const networkPercent = parsePercentage(methodology.network_time_allocation);
                const processingPercent = parsePercentage(methodology.processing_time_allocation);
                const total = networkPercent + processingPercent;

                return (
                  <div style={{
                    width: "100%",
                    height: "50px",
                    background: "rgba(59, 130, 246, 0.1)",
                    borderRadius: "8px",
                    display: "flex",
                    overflow: "hidden",
                    position: "relative"
                  }}>
                    <div
                      style={{
                        width: total > 0 ? `${(networkPercent / total) * 100}%` : "0%",
                        height: "100%",
                        background: "rgba(59, 130, 246, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRight: processingPercent > 0 ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
                        position: "relative"
                      }}
                      title={`${t("network")}: ${networkPercent}%`}
                    >
                      {(networkPercent / total) * 100 > 10 && (
                        <span style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "white"
                        }}>
                          {t("network").toUpperCase()} {networkPercent}%
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        width: total > 0 ? `${(processingPercent / total) * 100}%` : "0%",
                        height: "100%",
                        background: "rgba(139, 92, 246, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative"
                      }}
                      title={`${t("processing")}: ${processingPercent}%`}
                    >
                      {(processingPercent / total) * 100 > 10 && (
                        <span style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "white"
                        }}>
                          {t("processing").toUpperCase()} {processingPercent}%
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* 2 Column Grid for Tier Thresholds and Assumptions */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
              {/* Column 1: Tier Thresholds */}
              {methodology.tier_thresholds && (
                <div style={{ background: "rgba(139, 92, 246, 0.04)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(139, 92, 246, 0.12)" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "white", marginBottom: "14px", fontWeight: 600 }}>
                    {t("tierThresholds")}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {Object.entries(methodology.tier_thresholds).map(([tier, threshold], index) => {
                      const prettifiedTier = tier
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                      const tierColors = [
                        "rgba(34, 197, 94, 0.15)",
                        "rgba(59, 130, 246, 0.15)",
                        "rgba(245, 158, 11, 0.15)",
                        "rgba(239, 68, 68, 0.15)",
                        "rgba(139, 92, 246, 0.15)"
                      ];
                      const bgColor = tierColors[index % tierColors.length];
                      return (
                        <div key={tier} style={{
                          background: bgColor,
                          borderRadius: "8px",
                          padding: "12px 16px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}>
                          <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255, 255, 255, 0.9)", fontWeight: 600 }}>
                            {prettifiedTier}
                          </div>
                          <div style={{ fontSize: "18px", fontWeight: 600, color: "white" }}>
                            {threshold}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Column 2: Assumptions */}
              {methodology.assumptions && methodology.assumptions.length > 0 && (
                <div style={{ background: "rgba(245, 158, 11, 0.04)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(245, 158, 11, 0.12)" }}>
                  <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "white", marginBottom: "14px", fontWeight: 600 }}>
                    {t("assumptions")}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {methodology.assumptions.map((assumption, index) => (
                      <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px 14px", background: "rgba(245, 158, 11, 0.06)", borderRadius: "8px", border: "1px solid rgba(245, 158, 11, 0.1)" }}>
                        <i className="fas fa-chevron-right" style={{ fontSize: "10px", color: "#f59e0b", marginTop: "5px", flexShrink: 0 }}></i>
                        <span style={{ fontSize: "14px", color: "var(--card-text)", lineHeight: 1.5 }}>{assumption}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </SectionCardAccordion>
      )}

      {/* Response Time Breakdown Chart */}
      {recommendation_tables && recommendation_tables.length > 0 && (
        <SectionCardAccordion title={t("comprehensiveStepAnalysis")} icon={<i className="fas fa-chart-bar"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/* Chart Container */}
            <div style={{
              background: "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.03))",
              borderRadius: "16px",
              padding: "24px",
              border: "2px solid rgba(59, 130, 246, 0.2)",
              boxShadow: "0 4px 16px rgba(59, 130, 246, 0.1)"
            }}>
              <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px"
              }}>
                {/* Combined Chart - Bars with Line Graph Overlay */}
                <div style={{
                  background: "rgba(59, 130, 246, 0.04)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: "1px solid rgba(59, 130, 246, 0.15)"
                }}>
                  <div style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "var(--card-text)",
                    marginBottom: "16px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}>
                    {t("comprehensiveStepAnalysis")}
                  </div>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0"
                }}>
                  {/* Chart Area - Bars representing Response Time */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: "8px",
                    height: "400px",
                    alignItems: "flex-end",
                    padding: "20px 0 0 50px",
                    position: "relative"
                  }}>
                    {/* Y-axis labels for Response Time */}
                    {(() => {
                      const maxResponseTime = Math.max(...recommendation_tables.map(t => t.avg_response_time || 0));
                      const chartHeight = 400;
                      const padding = 20;
                      const usableHeight = chartHeight - (padding * 2);
                      
                      return (
                        <>
                          <div style={{
                            position: "absolute",
                            left: 0,
                            top: "20px",
                            bottom: 0,
                            width: "45px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "0"
                          }}>
                            <div style={{ fontSize: "10px", color: "var(--muted-text)", fontWeight: 600, textAlign: "right" }}>
                              {formatNumber(maxResponseTime)}ms
                            </div>
                            <div style={{ 
                              position: "absolute",
                              left: "50%",
                              top: "50%",
                              transform: "translate(-50%, -50%) rotate(-90deg)",
                              transformOrigin: "center",
                              fontSize: "11px",
                              color: "var(--card-text)",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em"
                            }}>
                              {t("responseTime")}
                            </div>
                            <div style={{ fontSize: "10px", color: "var(--muted-text)", fontWeight: 600, textAlign: "right" }}>
                              0ms
                            </div>
                          </div>
                          
                          {/* Grid lines for Y-axis */}
                          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                            const yPosition = padding + (usableHeight - (ratio * usableHeight));
                            return (
                              <div
                                key={`grid-${ratio}`}
                                style={{
                                  position: "absolute",
                                  left: "50px",
                                  right: "0",
                                  top: `${yPosition}px`,
                                  height: "1px",
                                  background: "rgba(59, 130, 246, 0.1)",
                                  zIndex: 0
                                }}
                              />
                            );
                          })}
                        </>
                      );
                    })()}

                    {recommendation_tables.map((table, index) => {
                      const scrollToStep = (stepIndex) => {
                        const element = document.getElementById(`rto-step-${stepIndex + 1}`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      };
                      
                      // Parse cost breakdown
                      const parseCost = (costStr) => {
                        if (!costStr) return 0;
                        const numStr = costStr.toString().replace(/[^0-9.]/g, '');
                        return parseFloat(numStr) || 0;
                      };

                      const costBreakdown = table.cost_breakdown || {};
                      const computeCost = parseCost(costBreakdown.compute);
                      const gpuCost = parseCost(costBreakdown.gpu);
                      const networkCost = parseCost(costBreakdown.network);
                      const cdnCost = parseCost(costBreakdown.cdn);
                      const monitoringCost = parseCost(costBreakdown.monitoring);
                      const totalCost = computeCost + gpuCost + networkCost + cdnCost + monitoringCost;

                      // Calculate bar height based on response time (Y-axis starts from 0)
                      const maxResponseTime = Math.max(...recommendation_tables.map(t => t.avg_response_time || 0));
                      const responseTime = table.avg_response_time || 0;
                      const normalizedResponseTime = maxResponseTime > 0 ? responseTime / maxResponseTime : 0;
                      const chartHeight = 400;
                      const padding = 20;
                      const usableHeight = chartHeight - (padding * 2);
                      
                      // Bar height represents response time (0 to maxResponseTime)
                      const barHeight = normalizedResponseTime * usableHeight;
                      
                      // Each bar is divided into 3 equal segments (33.33% each)
                      const segmentHeight = 33.33; // Each segment is 33.33% of the bar

                      return (
                        <div 
                          key={index}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                            maxWidth: "65px",
                            height: "100%",
                            justifyContent: "flex-end",
                            position: "relative",
                            zIndex: 1
                          }}
                        >
                          {/* Response Time Label - Above bar */}
                          <div style={{
                            position: "absolute",
                            top: `${padding + (usableHeight - (normalizedResponseTime * usableHeight)) - 25}px`,
                            left: "50%",
                            transform: "translateX(-50%)",
                            fontSize: "11px",
                            fontWeight: 700,
                            color: "#3b82f6",
                            background: "rgba(59, 130, 246, 0.15)",
                            padding: "4px 6px",
                            borderRadius: "6px",
                            border: "1px solid rgba(59, 130, 246, 0.3)",
                            whiteSpace: "nowrap",
                            zIndex: 1001,
                            pointerEvents: "none"
                          }}>
                            {formatNumber(responseTime)}ms
                          </div>
                          {/* Bar Container - Height based on response time */}
                          <div
                            style={{
                              width: "100%",
                              height: `${barHeight}px`,
                              minHeight: barHeight > 0 ? "60px" : "0",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "flex-end",
                              position: "relative",
                              background: "rgba(59, 130, 246, 0.05)",
                              borderRadius: "8px",
                              padding: "3px",
                              border: "1px solid rgba(59, 130, 246, 0.1)",
                              cursor: "pointer"
                            }}
                            onClick={() => scrollToStep(index)}
                          >
                            {/* Bar with 3 equal segments: CPU, RAM, Cost */}
                            <div style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px"
                            }}>
                              {/* CPU Section - 33.33% */}
                                    <div
                                      style={{
                                        width: "100%",
                                  height: `${segmentHeight}%`,
                                  background: "linear-gradient(135deg, #f97316, #ea580c)",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "2px 0",
                                  cursor: "pointer",
                                  minHeight: "20px"
                                  }}
                                  title={`${t("cpuCores")}: ${table.required_cpu_cores || 0} ${t("coresUnit")}`}
                                >
                                  <span style={{
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: "white",
                                    textShadow: "0 1px 3px rgba(0, 0, 0, 0.7)",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {table.required_cpu_cores || 0} {t("cpuUnit")}
                                  </span>
                                </div>

                              {/* RAM Section - 33.33% */}
                                <div
                                  style={{
                                    width: "100%",
                                  height: `${segmentHeight}%`,
                                  background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "2px 0",
                                  cursor: "pointer",
                                  minHeight: "20px"
                                  }}
                                  title={`${t("ramMemory")}: ${table.required_memory_gb || 0}${t("gbUnit")}`}
                                >
                                  <span style={{
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: "white",
                                    textShadow: "0 1px 3px rgba(0, 0, 0, 0.7)",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {table.required_memory_gb || 0}{t("gbUnit")} {t("ramUnit")}
                                  </span>
                                </div>

                              {/* Cost Section - 33.33% */}
                                <div
                                  style={{
                                    width: "100%",
                                  height: `${segmentHeight}%`,
                                  background: "linear-gradient(135deg, #10b981, #059669)",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "2px 0",
                                  cursor: "pointer",
                                  minHeight: "20px"
                                  }}
                                  title={`${t("estimatedMonthlyCost")}: ${table.estimated_monthly_cost || "$0"}`}
                                >
                                  <span style={{
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: "white",
                                    textShadow: "0 1px 3px rgba(0, 0, 0, 0.7)",
                                    whiteSpace: "nowrap"
                                  }}>
                                    ${totalCost > 0 ? totalCost.toLocaleString() : "0"}
                                  </span>
                                </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Labels Row - Below bars */}
                  <div style={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: "8px",
                    padding: "8px 0 0 50px"
                  }}>
                    {recommendation_tables.map((table, index) => {
                      const scrollToStep = (stepIndex) => {
                        const element = document.getElementById(`rto-step-${stepIndex + 1}`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      };

                      return (
                        <div key={`label-${index}`} style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "4px",
                          flex: 1,
                          maxWidth: "65px"
                        }}>
                          {/* Bar Label with Additional Info */}
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#3b82f6",
                              background: "rgba(59, 130, 246, 0.15)",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              border: "1px solid rgba(59, 130, 246, 0.3)",
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              textAlign: "center",
                              width: "100%"
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              scrollToStep(index);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.05)";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            {t("stepLabel")} {index + 1}
                          </div>
                          <div style={{
                            fontSize: "10px",
                            color: "var(--muted-text)",
                            marginTop: "2px",
                            textAlign: "center"
                          }}>
                            {table.performance_tier}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                </div>

                {/* Legend */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  paddingTop: "16px",
                  borderTop: "1px solid rgba(59, 130, 246, 0.2)"
                }}>
                  <div style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "var(--card-text)",
                    marginBottom: "4px"
                  }}>
                    {t("resources")}
                  </div>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "12px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{
                        width: "16px",
                        height: "16px",
                        background: "linear-gradient(135deg, #f97316, #ea580c)",
                        borderRadius: "3px"
                      }}></div>
                      <span style={{ fontSize: "11px", color: "var(--card-text)" }}>{t("cpuCores")}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{
                        width: "16px",
                        height: "16px",
                        background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                        borderRadius: "3px"
                      }}></div>
                      <span style={{ fontSize: "11px", color: "var(--card-text)" }}>{t("ramGb")}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{
                        width: "16px",
                        height: "16px",
                        background: "linear-gradient(135deg, #10b981, #059669)",
                        borderRadius: "3px"
                      }}></div>
                      <span style={{ fontSize: "11px", color: "var(--card-text)" }}>{t("monthlyCost")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCardAccordion>
      )}

      {/* Recommendation Tables */}
      {recommendation_tables && recommendation_tables.length > 0 && (
        <SectionCardAccordion title={t("optimizationRecommendations")} icon={<i className="fas fa-table"></i>}>
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {recommendation_tables.map((table, index) => {
              const tierColor = tierColors[table.performance_tier] || tierColors["Real-Time"];
              return (
                <SectionCardAccordion
                  key={index}
                  id={`rto-step-${index + 1}`}
                  title={`${t("stepLabel")} ${index + 1}: ${table.performance_tier}`}
                  icon={
                    <span style={{ color: "white", fontWeight: 700, fontSize: "16px" }}>
                      {index + 1}
                    </span>
                  }
                  iconStyle={{
                    borderRadius: "10px",
                    background: tierColor.gradient,
                    color: "white",
                  }}
                >
                  {/* Top-level fields - not grouped */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
                    {table.network_response_time && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "2px solid rgba(59, 130, 246, 0.2)",
                        boxShadow: "0 4px 12px rgba(59, 130, 246, 0.1)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <i className="fas fa-network-wired" style={{ color: "#3b82f6", fontSize: "14px" }}></i>
                          <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 700, letterSpacing: "0.05em" }}>{t("networkResponseTime")}</div>
                        </div>
                        <div style={{ fontSize: "18px", fontWeight: 700, color: "white", display: "flex", alignItems: "baseline", gap: "4px" }}>
                          {formatNumber(table.network_response_time)}
                          <span style={{ fontSize: "14px", fontWeight: 500, opacity: 0.9 }}>ms</span>
                        </div>
                      </div>
                    )}
                    {table.processing_time_budget && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(139, 92, 246, 0.05))",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "2px solid rgba(139, 92, 246, 0.2)",
                        boxShadow: "0 4px 12px rgba(139, 92, 246, 0.1)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <i className="fas fa-clock" style={{ color: "#8b5cf6", fontSize: "14px" }}></i>
                          <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.05em" }}>{t("processingTimeBudget")}</div>
                        </div>
                        <div style={{ fontSize: "18px", fontWeight: 700, color: "white", display: "flex", alignItems: "baseline", gap: "4px" }}>
                          {formatNumber(table.processing_time_budget)}
                          <span style={{ fontSize: "14px", fontWeight: 500, opacity: 0.9 }}>ms</span>
                        </div>
                      </div>
                    )}
                    {table.avg_response_time != null && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(100, 116, 139, 0.15), rgba(100, 116, 139, 0.06))",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "2px solid rgba(100, 116, 139, 0.25)",
                        boxShadow: "0 4px 12px rgba(100, 116, 139, 0.1)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <i className="fas fa-tachometer-alt" style={{ color: "#94a3b8", fontSize: "14px" }}></i>
                          <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#94a3b8", fontWeight: 700, letterSpacing: "0.05em" }}>{t("avgResponseTime")}</div>
                        </div>
                        <div style={{ fontSize: "18px", fontWeight: 700, color: "white", display: "flex", alignItems: "baseline", gap: "4px" }}>
                          {formatNumber(table.avg_response_time)}
                          <span style={{ fontSize: "14px", fontWeight: 500, opacity: 0.9 }}>ms</span>
                        </div>
                      </div>
                    )}
                    {table.cdn_strategy && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05))",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "2px solid rgba(34, 197, 94, 0.2)",
                        boxShadow: "0 4px 12px rgba(34, 197, 94, 0.1)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <i className="fas fa-globe" style={{ color: "#22c55e", fontSize: "14px" }}></i>
                          <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#22c55e", fontWeight: 700, letterSpacing: "0.05em" }}>{t("cdnStrategy")}</div>
                        </div>
                        <div style={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: "white"
                        }}>
                          {table.cdn_strategy}
                        </div>
                      </div>
                    )}
                    {table.peering_recommendation && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "2px solid rgba(245, 158, 11, 0.2)",
                        boxShadow: "0 4px 12px rgba(245, 158, 11, 0.1)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                          <i className="fas fa-exchange-alt" style={{ color: "#f59e0b", fontSize: "14px" }}></i>
                          <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#f59e0b", fontWeight: 700, letterSpacing: "0.05em" }}>{t("peeringRecommendation")}</div>
                        </div>
                        <div style={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: "white"
                        }}>
                          {table.peering_recommendation}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cost Breakdown - Single Stacked Bar */}
                  {(table.cost_breakdown || table.estimated_monthly_cost) && (() => {
                    // Parse cost values
                    const parseCost = (costStr) => {
                      if (!costStr) return 0;
                      const numStr = costStr.replace(/[^0-9.]/g, '');
                      return parseFloat(numStr) || 0;
                    };

                    const totalCost = table.estimated_monthly_cost ? parseCost(table.estimated_monthly_cost) : 0;
                    const breakdownItems = table.cost_breakdown ? Object.entries(table.cost_breakdown).map(([key, value]) => ({
                      label: key,
                      value: value,
                      amount: parseCost(value)
                    })) : [];

                    const breakdownSum = breakdownItems.reduce((sum, item) => sum + item.amount, 0);
                    const otherAmount = totalCost > breakdownSum ? totalCost - breakdownSum : 0;

                    if (otherAmount > 0) {
                      breakdownItems.push({
                        label: "other",
                        value: `$${otherAmount.toLocaleString()}`,
                        amount: otherAmount
                      });
                    }

                    const colors = [
                      "#3b82f6", // Blue
                      "#10b981", // Green
                      "#f59e0b", // Orange
                      "#8b5cf6", // Purple
                      "#ef4444", // Red
                      "#14b8a6", // Teal
                      "#ec4899", // Pink
                      "#6366f1"  // Indigo
                    ];

                    return (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(234, 179, 8, 0.08), rgba(234, 179, 8, 0.03))",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "2px solid rgba(234, 179, 8, 0.2)",
                        boxShadow: "0 4px 16px rgba(234, 179, 8, 0.1)",
                        marginTop: "16px"
                      }}>
                        <div style={{
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: "#eab308",
                          marginBottom: "16px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: "linear-gradient(135deg, #eab308, #ca8a04)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "14px"
                            }}>
                              <i className="fas fa-dollar-sign"></i>
                            </div>
                            {t("costBreakdown")}
                          </div>
                          {table.estimated_monthly_cost && (
                            <div style={{ fontSize: "16px", fontWeight: 700, color: "#eab308" }}>
                              {table.estimated_monthly_cost}
                            </div>
                          )}
                        </div>
                        <div style={{
                          width: "100%",
                          height: "40px",
                          background: "rgba(234, 179, 8, 0.1)",
                          borderRadius: "8px",
                          display: "flex",
                          overflow: "hidden",
                          position: "relative"
                        }}>
                          {breakdownItems.map((item, index) => {
                            const percentage = totalCost > 0 ? (item.amount / totalCost) * 100 : 0;
                            const isOther = item.label.toLowerCase() === "other";
                            const color = isOther ? "#6b7280" : colors[index % colors.length];

                            return (
                              <div
                                key={item.label}
                                style={{
                                  width: `${percentage}%`,
                                  height: "100%",
                                  background: color,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRight: index < breakdownItems.length - 1 ? "1px solid rgba(0, 0, 0, 0.1)" : "none",
                                  position: "relative",
                                  minWidth: percentage > 5 ? "auto" : "0"
                                }}
                                title={`${item.label.toLowerCase() === "other" ? t("other") : item.label}: ${item.value}`}
                              >
                                {percentage > 8 && (
                                  <span style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "white",
                                    textTransform: "uppercase",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    padding: "0 4px"
                                  }}>
                                    {item.label.toLowerCase() === "other" ? t("other") : item.label}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "16px",
                          marginTop: "16px",
                          fontSize: "14px"
                        }}>
                          {breakdownItems.map((item, index) => {
                            const isOther = item.label.toLowerCase() === "other";
                            const color = isOther ? "#6b7280" : colors[index % colors.length];
                            return (
                              <div key={item.label} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{
                                  width: "16px",
                                  height: "16px",
                                  background: color,
                                  borderRadius: "3px"
                                }}></div>
                                <span style={{
                                  textTransform: "uppercase",
                                  color: "#eab308",
                                  fontWeight: 600
                                }}>
                                  {item.label.toLowerCase() === "other" ? t("other") : item.label}: {item.value}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* System Requirements and SD-WAN Config Side by Side */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", alignItems: "stretch" }}>
                    {/* System Requirements */}
                    {(table.required_memory_gb || table.required_cpu_cores || table.gpu_recommendation || table.container_scaling || table.storage_recommendation || table.network_bandwidth || table.protocol_optimizations) && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.03))",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "2px solid rgba(59, 130, 246, 0.2)",
                        boxShadow: "0 4px 16px rgba(59, 130, 246, 0.1)"
                      }}>
                        <div style={{
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: "#3b82f6",
                          marginBottom: "16px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <div style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "14px"
                          }}>
                            <i className="fas fa-server"></i>
                          </div>
                          {t("systemRequirements")}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
                          {table.required_memory_gb && (
                            <div style={{
                              background: "rgba(59, 130, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("requiredMemoryGb")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.required_memory_gb % 1 === 0 ? Math.floor(table.required_memory_gb) : table.required_memory_gb}
                              </div>
                            </div>
                          )}
                          {table.required_cpu_cores && (
                            <div style={{
                              background: "rgba(59, 130, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("requiredCpuCores")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.required_cpu_cores % 1 === 0 ? Math.floor(table.required_cpu_cores) : table.required_cpu_cores}
                              </div>
                            </div>
                          )}
                          {table.gpu_recommendation && (
                            <div style={{
                              background: "rgba(59, 130, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("gpuRecommendation")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>{table.gpu_recommendation}</div>
                            </div>
                          )}
                          {table.container_scaling && (
                            <div style={{
                              background: "rgba(59, 130, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("containerScaling")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>{table.container_scaling}</div>
                            </div>
                          )}
                          {table.storage_recommendation && (
                            <div style={{
                              background: "rgba(59, 130, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("storageRecommendation")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>{table.storage_recommendation}</div>
                            </div>
                          )}
                          {table.network_bandwidth && (
                            <div style={{
                              background: "rgba(59, 130, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("networkBandwidth")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>{table.network_bandwidth}</div>
                            </div>
                          )}
                          {table.protocol_optimizations && table.protocol_optimizations.length > 0 && (
                            <div style={{
                              background: "rgba(59, 130, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#3b82f6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("protocolOptimizations")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>{table.protocol_optimizations.join(", ")}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* SD-WAN Config */}
                    {table.sdwan_config && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(245, 158, 11, 0.03))",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "2px solid rgba(245, 158, 11, 0.2)",
                        boxShadow: "0 4px 16px rgba(245, 158, 11, 0.1)"
                      }}>
                        <div style={{
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: "#f59e0b",
                          marginBottom: "16px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <div style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "linear-gradient(135deg, #f59e0b, #d97706)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "14px"
                          }}>
                            <i className="fas fa-sitemap"></i>
                          </div>
                          {t("sdwanConfig")}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
                          {table.sdwan_config.primary && (
                            <div style={{
                              background: "rgba(245, 158, 11, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#f59e0b", fontWeight: 600, letterSpacing: "0.05em" }}>{t("primary")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sdwan_config.primary}
                              </div>
                            </div>
                          )}
                          {table.sdwan_config.backup && (
                            <div style={{
                              background: "rgba(245, 158, 11, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#f59e0b", fontWeight: 600, letterSpacing: "0.05em" }}>{t("backup")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sdwan_config.backup}
                              </div>
                            </div>
                          )}
                          {table.sdwan_config.qos && (
                            <div style={{
                              background: "rgba(245, 158, 11, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#f59e0b", fontWeight: 600, letterSpacing: "0.05em" }}>{t("qos")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sdwan_config.qos}
                              </div>
                            </div>
                          )}
                          {table.sdwan_config.forwarding && (
                            <div style={{
                              background: "rgba(245, 158, 11, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#f59e0b", fontWeight: 600, letterSpacing: "0.05em" }}>{t("forwarding")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sdwan_config.forwarding}
                              </div>
                            </div>
                          )}
                          {table.sdwan_config.security && (
                            <div style={{
                              background: "rgba(245, 158, 11, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#f59e0b", fontWeight: 600, letterSpacing: "0.05em" }}>{t("security")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sdwan_config.security}
                              </div>
                            </div>
                          )}
                          {table.sdwan_config.optimization && (
                            <div style={{
                              background: "rgba(245, 158, 11, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#f59e0b", fontWeight: 600, letterSpacing: "0.05em" }}>{t("optimization")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sdwan_config.optimization}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* TCP Tuning Params and SLA Targets Side by Side */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", alignItems: "stretch" }}>
                    {/* TCP Tuning Params */}
                    {table.tcp_tuning_params && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(20, 184, 166, 0.08), rgba(20, 184, 166, 0.03))",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "2px solid rgba(20, 184, 166, 0.2)",
                        boxShadow: "0 4px 16px rgba(20, 184, 166, 0.1)",
                        display: "flex",
                        flexDirection: "column"
                      }}>
                        <div style={{
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: "#14b8a6",
                          marginBottom: "16px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <div style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "linear-gradient(135deg, #14b8a6, #0d9488)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "14px"
                          }}>
                            <i className="fas fa-cog"></i>
                          </div>
                          {t("tcpTuningParams")}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", flex: 1 }}>
                          {table.tcp_tuning_params.window_scaling && (
                            <div style={{
                              background: "rgba(20, 184, 166, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#14b8a6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("windowScaling")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.tcp_tuning_params.window_scaling}
                              </div>
                            </div>
                          )}
                          {table.tcp_tuning_params.initial_congestion_window && (
                            <div style={{
                              background: "rgba(20, 184, 166, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#14b8a6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("initialCongestionWindow")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.tcp_tuning_params.initial_congestion_window}
                              </div>
                            </div>
                          )}
                          {table.tcp_tuning_params.tcp_timestamps && (
                            <div style={{
                              background: "rgba(20, 184, 166, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#14b8a6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("tcpTimestamps")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.tcp_tuning_params.tcp_timestamps}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* SLA Targets */}
                    {table.sla_targets && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(236, 72, 153, 0.08), rgba(236, 72, 153, 0.03))",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "2px solid rgba(236, 72, 153, 0.2)",
                        boxShadow: "0 4px 16px rgba(236, 72, 153, 0.1)",
                        display: "flex",
                        flexDirection: "column"
                      }}>
                        <div style={{
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: "#ec4899",
                          marginBottom: "16px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <div style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "linear-gradient(135deg, #ec4899, #db2777)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "14px"
                          }}>
                            <i className="fas fa-bullseye"></i>
                          </div>
                          {t("slaTargets")}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", flex: 1 }}>
                          {table.sla_targets.p95_latency && (
                            <div style={{
                              background: "rgba(236, 72, 153, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ec4899", fontWeight: 600, letterSpacing: "0.05em" }}>{t("p95Latency")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sla_targets.p95_latency % 1 === 0 ? Math.floor(table.sla_targets.p95_latency) : table.sla_targets.p95_latency}ms
                              </div>
                            </div>
                          )}
                          {table.sla_targets.p99_latency && (
                            <div style={{
                              background: "rgba(236, 72, 153, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ec4899", fontWeight: 600, letterSpacing: "0.05em" }}>{t("p99Latency")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sla_targets.p99_latency % 1 === 0 ? Math.floor(table.sla_targets.p99_latency) : table.sla_targets.p99_latency}ms
                              </div>
                            </div>
                          )}
                          {table.sla_targets.availability && (
                            <div style={{
                              background: "rgba(236, 72, 153, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ec4899", fontWeight: 600, letterSpacing: "0.05em" }}>{t("availability")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sla_targets.availability % 1 === 0 ? Math.floor(table.sla_targets.availability) : table.sla_targets.availability}%
                              </div>
                            </div>
                          )}
                          {table.sla_targets.error_rate && (
                            <div style={{
                              background: "rgba(236, 72, 153, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ec4899", fontWeight: 600, letterSpacing: "0.05em" }}>{t("errorRate")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.sla_targets.error_rate % 1 === 0 ? Math.floor(table.sla_targets.error_rate) : table.sla_targets.error_rate}%
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Shortest Path Recommendation and Infrastructure Recommendation Side by Side */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px", alignItems: "stretch" }}>
                    {/* Shortest Path Recommendation */}
                    {table.shortest_path_recommendation && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(34, 197, 94, 0.03))",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "2px solid rgba(34, 197, 94, 0.2)",
                        boxShadow: "0 4px 16px rgba(34, 197, 94, 0.1)",
                        display: "flex",
                        flexDirection: "column"
                      }}>
                        <div style={{
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: "#22c55e",
                          marginBottom: "16px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <div style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "linear-gradient(135deg, #22c55e, #16a34a)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "14px"
                          }}>
                            <i className="fas fa-route"></i>
                          </div>
                          {t("shortestPathRecommendation")}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", flex: 1 }}>
                          {(() => {
                            const pathItems = [
                              { key: "routing", label: t("routing"), value: table.shortest_path_recommendation.routing },
                              { key: "cdn", label: t("cdn"), value: table.shortest_path_recommendation.cdn },
                              { key: "peering", label: t("peering"), value: table.shortest_path_recommendation.peering },
                              { key: "edge_locations", label: t("edgeLocations"), value: table.shortest_path_recommendation.edge_locations },
                              { key: "load_balancing", label: t("loadBalancing"), value: table.shortest_path_recommendation.load_balancing },
                              { key: "dns", label: t("dns"), value: table.shortest_path_recommendation.dns }
                            ].filter(item => item.value);

                            return pathItems.map((item) => (
                              <div key={item.key} style={{
                                background: "rgba(34, 197, 94, 0.15)",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px"
                              }}>
                                <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#22c55e", fontWeight: 600, letterSpacing: "0.05em" }}>{item.label}</div>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                  {item.value}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Infrastructure Recommendation */}
                    {table.infrastructure_recommendation && (
                      <div style={{
                        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(139, 92, 246, 0.03))",
                        borderRadius: "16px",
                        padding: "20px",
                        border: "2px solid rgba(139, 92, 246, 0.2)",
                        boxShadow: "0 4px 16px rgba(139, 92, 246, 0.1)",
                        display: "flex",
                        flexDirection: "column"
                      }}>
                        <div style={{
                          fontSize: "13px",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: "#8b5cf6",
                          marginBottom: "16px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px"
                        }}>
                          <div style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "14px"
                          }}>
                            <i className="fas fa-cloud"></i>
                          </div>
                          {t("infrastructureRecommendation")}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", flex: 1 }}>
                          {table.infrastructure_recommendation.deployment_model && (
                            <div style={{
                              background: "rgba(139, 92, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#8b5cf6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("deploymentModel")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.infrastructure_recommendation.deployment_model}
                              </div>
                            </div>
                          )}
                          {table.infrastructure_recommendation.orchestration && (
                            <div style={{
                              background: "rgba(139, 92, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#8b5cf6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("orchestration")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.infrastructure_recommendation.orchestration}
                              </div>
                            </div>
                          )}
                          {table.infrastructure_recommendation.database && (
                            <div style={{
                              background: "rgba(139, 92, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#8b5cf6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("database")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.infrastructure_recommendation.database}
                              </div>
                            </div>
                          )}
                          {table.infrastructure_recommendation.message_queue && (
                            <div style={{
                              background: "rgba(139, 92, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#8b5cf6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("messageQueue")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.infrastructure_recommendation.message_queue}
                              </div>
                            </div>
                          )}
                          {table.infrastructure_recommendation.observability && (
                            <div style={{
                              background: "rgba(139, 92, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#8b5cf6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("observability")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.infrastructure_recommendation.observability}
                              </div>
                            </div>
                          )}
                          {table.infrastructure_recommendation.disaster_recovery && (
                            <div style={{
                              background: "rgba(139, 92, 246, 0.15)",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px"
                            }}>
                              <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#8b5cf6", fontWeight: 600, letterSpacing: "0.05em" }}>{t("disasterRecovery")}</div>
                              <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                {table.infrastructure_recommendation.disaster_recovery}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Monitoring Config - Full Width */}
                  {table.monitoring_config && (
                    <div style={{
                      background: "linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(99, 102, 241, 0.03))",
                      borderRadius: "16px",
                      padding: "20px",
                      border: "2px solid rgba(99, 102, 241, 0.2)",
                      boxShadow: "0 4px 16px rgba(99, 102, 241, 0.1)",
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <div style={{
                        fontSize: "13px",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: "#6366f1",
                        marginBottom: "16px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                      }}>
                        <div style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "14px"
                        }}>
                          <i className="fas fa-chart-line"></i>
                        </div>
                        {t("monitoringConfig")}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", flex: 1 }}>
                        {/* Column 1: Metrics Frequency */}
                        {table.monitoring_config.metrics_frequency && (
                          <div style={{
                            background: "rgba(99, 102, 241, 0.15)",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px"
                          }}>
                            <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#6366f1", fontWeight: 600, letterSpacing: "0.05em" }}>{t("metricsFrequency")}</div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                              {table.monitoring_config.metrics_frequency}
                            </div>
                          </div>
                        )}
                        {/* Column 2: Synthetic Monitoring */}
                        {table.monitoring_config.synthetic_monitoring && (
                          <div style={{
                            background: "rgba(99, 102, 241, 0.15)",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px"
                          }}>
                            <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#6366f1", fontWeight: 600, letterSpacing: "0.05em" }}>{t("syntheticMonitoring")}</div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                              {table.monitoring_config.synthetic_monitoring}
                            </div>
                          </div>
                        )}
                        {/* Column 3: Real User Monitoring */}
                        {table.monitoring_config.real_user_monitoring && (
                          <div style={{
                            background: "rgba(99, 102, 241, 0.15)",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px"
                          }}>
                            <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#6366f1", fontWeight: 600, letterSpacing: "0.05em" }}>{t("realUserMonitoring")}</div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                              {table.monitoring_config.real_user_monitoring}
                            </div>
                          </div>
                        )}
                        {/* Column 4: AI Anomaly Detection */}
                        {table.monitoring_config.ai_anomaly_detection && (
                          <div style={{
                            background: "rgba(99, 102, 241, 0.15)",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px"
                          }}>
                            <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#6366f1", fontWeight: 600, letterSpacing: "0.05em" }}>{t("aiAnomalyDetection")}</div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                              {table.monitoring_config.ai_anomaly_detection}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Alert Thresholds - Below the 4 columns */}
                      {table.monitoring_config.alert_thresholds && (
                        <div style={{ marginTop: "16px" }}>
                          <div style={{
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.1em",
                            color: "#ef4444",
                            marginBottom: "12px",
                            fontWeight: 700,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}>
                            <i className="fas fa-exclamation-triangle" style={{ fontSize: "12px" }}></i>
                            {t("alertThresholds")}
                          </div>
                          <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "12px"
                          }}>
                            {table.monitoring_config.alert_thresholds.latency_p95 && (
                              <div style={{
                                background: "rgba(239, 68, 68, 0.15)",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px"
                              }}>
                                <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ef4444", fontWeight: 600, letterSpacing: "0.05em" }}>{t("p95Latency")}</div>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                  {table.monitoring_config.alert_thresholds.latency_p95}ms
                                </div>
                              </div>
                            )}
                            {table.monitoring_config.alert_thresholds.error_rate && (
                              <div style={{
                                background: "rgba(239, 68, 68, 0.15)",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px"
                              }}>
                                <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ef4444", fontWeight: 600, letterSpacing: "0.05em" }}>{t("errorRate")}</div>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                  {table.monitoring_config.alert_thresholds.error_rate}
                                </div>
                              </div>
                            )}
                            {table.monitoring_config.alert_thresholds.throughput_drop && (
                              <div style={{
                                background: "rgba(239, 68, 68, 0.15)",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px"
                              }}>
                                <div style={{ fontSize: "12px", textTransform: "uppercase", color: "#ef4444", fontWeight: 600, letterSpacing: "0.05em" }}>{t("throughputDrop")}</div>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "white" }}>
                                  {table.monitoring_config.alert_thresholds.throughput_drop}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </SectionCardAccordion>
              );
            })}
          </div>
        </SectionCardAccordion>
      )}
    </div>
  );
};

export default RtoRecommendations;
